import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getMELEntries, getMELItems, getMELProcedures } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { getMelApplicabilityTag } from '../utils/melApplicability';
import './DocumentsFolderPage.css';

const OPERATORS = [
  { code: 'WZZ', label: 'WZZ', name: 'Wizz Air Hungary', registrations: 'HA- / G-' },
  { code: 'WMT', label: 'WMT', name: 'Wizz Air Malta', registrations: '9H-' }
];

const DOCUMENT_TYPES = [
  { id: 'entries', label: 'MEL Entries', title: 'MEL Entries' },
  { id: 'mel', label: 'MEL Items', title: 'MEL Items' },
  { id: 'procedures', label: 'MEL OPS PROC', title: 'MEL OPS PROC' }
];

const PINNED_MELS_STORAGE_KEY = 'etlb_pinned_mels';
const DOCUMENTS_VIEW_STORAGE_KEY = 'etlb_documents_view';
const EMPTY_DOCUMENT_PATH = { chapter: '', section: '', subsection: '', base: '' };

const loadDocumentsView = () => {
  try {
    const stored = window.sessionStorage.getItem(DOCUMENTS_VIEW_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    return {};
  }
};

const saveDocumentsView = (view) => {
  try {
    window.sessionStorage.setItem(DOCUMENTS_VIEW_STORAGE_KEY, JSON.stringify(view));
  } catch (error) {
    // Session storage may be unavailable in restricted browser modes.
  }
};

const getOperatorForRegistration = (registration) => (
  String(registration || '').trim().toUpperCase().startsWith('9H-') ? 'WMT' : 'WZZ'
);

const formatApplicability = (value) => (
  String(value || '')
    .split(/[,;|\n]+/)
    .map(item => item.trim())
    .filter(Boolean)
);

const splitInstalledRequired = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return { numberInstalled: '', numberRequired: '' };

  const spacedParts = raw
    .split(/(?:\s+|\/)+/)
    .map(part => part.trim())
    .filter(Boolean);

  if (spacedParts.length >= 2) {
    return { numberInstalled: spacedParts[0], numberRequired: spacedParts[1] };
  }

  const compact = raw.replace(/[^\dA-Za-z]/g, '');
  if (!compact) return { numberInstalled: '', numberRequired: '' };
  if (!/^\d+$/.test(compact)) return { numberInstalled: compact, numberRequired: '' };
  if (compact.length === 1) return { numberInstalled: compact, numberRequired: '' };
  if (compact.length === 2) return { numberInstalled: compact.slice(0, 1), numberRequired: compact.slice(1) };
  if (compact.length === 3) return { numberInstalled: compact.slice(0, 2), numberRequired: compact.slice(2) };
  if (compact.endsWith('0')) return { numberInstalled: compact.slice(0, -1), numberRequired: compact.slice(-1) };

  const splitAt = Math.ceil(compact.length / 2);
  return { numberInstalled: compact.slice(0, splitAt), numberRequired: compact.slice(splitAt) };
};

const getMelRequirementData = (item) => {
  const fallback = splitInstalledRequired(item?.installed_required);
  return {
    repairInterval: item?.repair_interval || item?.mel_type || '-',
    numberInstalled: item?.number_installed || fallback.numberInstalled || '-',
    numberRequired: item?.number_required || fallback.numberRequired || '-',
    placard: item?.placard_required === true ? 'Yes' : item?.placard_required === false ? 'No' : '-'
  };
};

const renderMelRequirementTable = (item, className = '') => {
  const data = getMelRequirementData(item);

  return (
    <div className={`documents-detail-section documents-mel-table-section ${className}`.trim()}>
      <span>MEL Item Limits</span>
      <div className="documents-mel-table-wrap">
        <table className="documents-mel-table">
          <thead>
            <tr>
              <th>Repair interval</th>
              <th>Nbr installed</th>
              <th>Nbr required</th>
              <th>Placard</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{data.repairInterval}</td>
              <td>{data.numberInstalled}</td>
              <td>{data.numberRequired}</td>
              <td>{data.placard}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

const renderMelDetailInlineText = (text) => String(text || '');

const normalizePdfDisplayColor = (color) => {
  const value = String(color || '').trim().toLowerCase();
  if (!value || value === '#000000' || value === '#ffffff') return '';
  if (value === '#5f5f5f' || value === '#666666') return '#aeb7c8';
  if (value === '#ff9900') return '#ffb33f';
  return value;
};

const getPdfColorClass = (color) => {
  const value = String(color || '').trim().toLowerCase();
  if (value === '#ff9900') return 'pdf-color-orange';
  if (value === '#5f5f5f' || value === '#666666') return 'pdf-color-muted';
  if (value === '#ff0000' || value === '#c00000') return 'pdf-color-red';
  if (value === '#00b050' || value === '#008000') return 'pdf-color-green';
  if (value === '#0070c0' || value === '#0000ff') return 'pdf-color-blue';
  return '';
};

const isMarkerSpan = (text) => /^(?:\([om]\)(?:\([om]\))?|\d+\)|[-•])$/.test(String(text || '').trim());

const renderStyledPdfSpans = (spans = []) => {
  if (!Array.isArray(spans) || !spans.length) return null;

  return spans.map((span, index) => {
    const color = normalizePdfDisplayColor(span.color);
    const colorClass = getPdfColorClass(span.color);
    const previousSpan = spans[index - 1];
    const nextSpan = spans[index + 1];
    const spanText = String(span.text || '');
    const previousText = String(previousSpan?.text || '');
    const geometricGap = Number(span.x) - Number(previousSpan?.right);
    const minimumWordGap = Math.max(0.8, Math.min(Number(span.size) || 9, Number(previousSpan?.size) || 9) * 0.12);
    const needsLeadingSpace = Boolean(
      previousSpan &&
      !/\s$/.test(previousText) &&
      !/^\s/.test(spanText) &&
      Number.isFinite(geometricGap) &&
      geometricGap >= minimumWordGap
    );
    const text = `${needsLeadingSpace ? ' ' : ''}${spanText}${isMarkerSpan(spanText) && nextSpan?.text && !/^\s/.test(nextSpan.text) ? ' ' : ''}`;
    const style = {
      ...(color ? { color } : {}),
      ...(span.bold ? { fontWeight: 600 } : {}),
      ...(span.italic ? { fontStyle: 'italic' } : {})
    };

    return (
      <span key={`${index}-${span.text}-${span.x || 0}`} className={`documents-pdf-span ${colorClass}`} style={style}>
        {text}
      </span>
    );
  });
};

const getRichMelLineType = (line) => {
  const value = String(line || '').trim();
  if (/^Reference\(s\)$/i.test(value)) return 'references-title';
  if (/^\([om]\)\s*Refer to/i.test(value)) return 'reference';
  if (/^Note:/i.test(value)) return 'note';
  if (/^\d+\)\s*/.test(value)) return 'numbered';
  if (/^\([om]\)(?:\([om]\))?\s*/.test(value)) return 'condition';
  return 'body';
};

const getCautionLabel = (block) => {
  if (block?.type !== 'line') return '';
  const firstSpan = String(block.spans?.[0]?.text || '').trim();
  const plain = String(block.plain || block.text || '').trim();
  const match = (firstSpan || plain).match(/^(CAUTION|WARNING)\b/i);
  return match ? match[1].toUpperCase() : '';
};

const groupCautionBlocks = (blocks) => {
  const grouped = [];

  for (let index = 0; index < blocks.length; index += 1) {
    const block = blocks[index];
    const label = getCautionLabel(block);
    if (!label) {
      grouped.push(block);
      continue;
    }

    const firstSpans = Array.isArray(block.spans) ? block.spans.slice(1) : [];
    const firstText = String(block.text || block.plain || '')
      .replace(/^(?:CAUTION|WARNING)\s*:?[ ]*/i, '')
      .trim();
    const body = firstText ? [{ ...block, text: firstText, plain: firstText, spans: firstSpans }] : [];
    const cautionIndent = Number(block.indent) || 0;

    while (index + 1 < blocks.length && body.length < 6) {
      const candidate = blocks[index + 1];
      if (candidate?.type !== 'line' || getCautionLabel(candidate)) break;
      const candidateIndent = Number(candidate.indent) || 0;
      const italic = candidate.spans?.some(span => span.italic);
      if (!italic && candidateIndent <= cautionIndent + 4) break;
      body.push(candidate);
      index += 1;
    }

    grouped.push({ type: 'caution', label, body });
  }

  return grouped;
};

const groupNoteBlocks = (blocks) => {
  const grouped = [];

  for (let index = 0; index < blocks.length; index += 1) {
    const block = blocks[index];
    if (block?.type !== 'line' || !/^Note:/i.test(String(block.plain || block.text || '').trim())) {
      grouped.push(block);
      continue;
    }

    const firstSpans = Array.isArray(block.spans) ? block.spans.slice(1) : [];
    const firstText = String(block.text || block.plain || '')
      .replace(/^Note:\s*/i, '')
      .trim();
    const body = firstText ? [{ ...block, text: firstText, plain: firstText, spans: firstSpans }] : [];
    const noteIndent = Number(block.indent) || 0;

    while (index + 1 < blocks.length && body.length < 8) {
      const candidate = blocks[index + 1];
      if (candidate?.type !== 'line' || getCautionLabel(candidate)) break;
      const candidateIndent = Number(candidate.indent) || 0;
      const italic = candidate.spans?.some(span => span.italic);
      if (!italic || (body.length && candidateIndent <= noteIndent + 4)) break;
      body.push(candidate);
      index += 1;
    }

    grouped.push({ type: 'note', label: 'Note:', body });
  }

  return grouped;
};

const splitEntryTableCell = (cell = {}) => {
  const paragraphs = String(cell.text || '')
    .split(/\n\s*\n/)
    .map(text => text.trim())
    .filter(Boolean);

  if (paragraphs.length <= 1) return [{ ...cell }];

  const groups = paragraphs.map(text => ({ text, spans: [] }));
  const targetLengths = paragraphs.map(text => text.replace(/\s/g, '').length);
  let groupIndex = 0;
  let groupLength = 0;

  (cell.spans || []).forEach(span => {
    const normalizedLength = String(span.text || '').replace(/\s/g, '').length;
    const firstSpanInGroup = groups[groupIndex].spans.length === 0;
    groups[groupIndex].spans.push({
      ...span,
      text: firstSpanInGroup ? String(span.text || '').replace(/^\s+/, '') : span.text
    });
    groupLength += normalizedLength;

    if (groupLength >= targetLengths[groupIndex] && groupIndex < groups.length - 1) {
      groupIndex += 1;
      groupLength = 0;
    }
  });

  return groups;
};

const expandEntryTableRows = (rows = []) => rows.flatMap((row, rowIndex) => {
  if (rowIndex === 0 || !Array.isArray(row) || row.length < 2) return [row];
  const splitCells = row.map(splitEntryTableCell);
  const expandedRowCount = Math.max(...splitCells.map(cells => cells.length));
  if (expandedRowCount <= 1) return [row];

  return Array.from({ length: expandedRowCount }, (_, expandedIndex) => (
    splitCells.map(cells => cells[expandedIndex] || { text: '', spans: [] })
  ));
});

const getDocumentReference = text => {
  const value = String(text || '').replace(/\s+/g, ' ').trim();
  const procedureMatch = value.match(/\bOps\s*Proc\s+(\d{2}(?:-\d{2}){2,3}[A-Z]?)/i);
  if (procedureMatch) return { documentType: 'procedures', reference: procedureMatch[1].toUpperCase() };

  const itemMatch = value.match(/\b(?:MEL\s+)?Item\s+(\d{2}(?:-\d{2}){2,3}[A-Z]?)/i);
  if (itemMatch) return { documentType: 'mel', reference: itemMatch[1].toUpperCase() };
  return null;
};

const renderDocumentReference = (text, content, onReferenceClick) => {
  const reference = getDocumentReference(text);
  if (!reference || !onReferenceClick) return content;
  return (
    <button
      type="button"
      className="documents-cross-reference"
      onClick={() => onReferenceClick(reference)}
      title={`Open ${reference.reference}`}
    >
      {content}
    </button>
  );
};

const renderRichDocumentBlocks = (blocks, mode = 'mel', compact = false, onReferenceClick = null) => {
  if (!Array.isArray(blocks) || !blocks.length) return null;
  const displayBlocks = groupNoteBlocks(groupCautionBlocks(blocks));

  return (
    <div className={`documents-rich-text ${mode === 'procedure' ? 'is-procedure' : 'is-mel'} ${compact ? 'compact' : ''}`}>
      {displayBlocks.map((block, index) => {
        if (block?.type === 'note') {
          return (
            <aside key={`rich-note-${index}`} className="documents-rich-note">
              <div className="documents-rich-note-label">{block.label}</div>
              <div className="documents-rich-note-body">
                {(block.body || []).map((line, lineIndex) => (
                  <div key={`${lineIndex}-${line.plain || line.text}`}>
                    {renderDocumentReference(
                      line.plain || line.text,
                      renderStyledPdfSpans(line.spans) || line.text,
                      onReferenceClick
                    )}
                  </div>
                ))}
              </div>
            </aside>
          );
        }

        if (block?.type === 'caution') {
          return (
            <aside key={`rich-caution-${index}`} className={`documents-rich-caution is-${block.label.toLowerCase()}`}>
              <div className="documents-rich-caution-label">{block.label}</div>
              <div className="documents-rich-caution-body">
                {(block.body || []).map((line, lineIndex) => (
                  <div key={`${lineIndex}-${line.plain || line.text}`}>
                    {renderDocumentReference(
                      line.plain || line.text,
                      renderStyledPdfSpans(line.spans) || line.text,
                      onReferenceClick
                    )}
                  </div>
                ))}
              </div>
            </aside>
          );
        }

        if (block?.type === 'figure' && block.src) {
          return (
            <figure key={`rich-figure-${index}`} className="documents-rich-figure">
              <img
                className="documents-rich-figure-image"
                src={block.src}
                alt={block.alt || 'MEL chart'}
                loading="lazy"
              />
            </figure>
          );
        }

        if (block?.type === 'table') {
          const tableRows = mode === 'entry' ? expandEntryTableRows(block.rows || []) : (block.rows || []);
          return (
            <div key={`rich-table-${index}`} className="documents-rich-table-wrap">
              <table className="documents-rich-table">
                <tbody>
                  {tableRows.map((row, rowIndex) => (
                    <tr key={`${rowIndex}-${row.map(cell => cell.text).join('|')}`}>
                      {row.map((cell, cellIndex) => (
                        <td key={`${cellIndex}-${cell.text}`}>
                          {renderDocumentReference(
                            cell.text,
                            renderStyledPdfSpans(cell.spans) || cell.text,
                            onReferenceClick
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }

        const plain = block?.plain || block?.text || '';
        const type = mode === 'procedure'
          ? getRichProcedureLineType(displayBlocks, index, plain)
          : getRichMelLineType(plain);
        const indent = Math.max(0, Math.min(96, Number(block?.indent) || 0));

        if (mode === 'procedure' && type === 'check') {
          const contentSpans = Array.isArray(block.spans) && isProcedureBulletPrefix(block.spans[0]?.text || '')
            ? block.spans.slice(1)
            : block.spans;
          const emphasized = contentSpans?.some(span => span.bold);

          return (
            <div
              key={`rich-line-${index}-${plain.slice(0, 24)}`}
              className={`documents-rich-line type-check ${emphasized ? 'is-emphasis' : ''}`}
              style={{ paddingLeft: `${indent}px` }}
            >
              <span className="documents-procedure-marker" aria-hidden="true">{emphasized ? '\u25A0' : '-'}</span>
              <span className="documents-procedure-line-content">
                {renderDocumentReference(
                  plain,
                  renderStyledPdfSpans(contentSpans) || stripProcedureBulletPrefix(block.text || plain),
                  onReferenceClick
                )}
              </span>
            </div>
          );
        }

        return (
          <div
            key={`rich-line-${index}-${plain.slice(0, 24)}`}
            className={`documents-rich-line type-${type}`}
            style={{ paddingLeft: `${indent}px` }}
          >
            {renderDocumentReference(
              plain,
              renderStyledPdfSpans(block.spans) || block.text,
              onReferenceClick
            )}
          </div>
        );
      })}
    </div>
  );
};

const getMelDetailLineType = (line) => {
  const value = String(line || '').trim();
  if (/^Reference\(s\)$/i.test(value)) return 'references-title';
  if (/^\([om]\)\s+Refer to/i.test(value)) return 'reference';
  if (/^Note:/i.test(value)) return 'note';
  if (/^\d+\)\s+/.test(value)) return 'numbered';
  if (/^\([om]\)(?:\([om]\))?\s+/.test(value)) return 'condition';
  return 'body';
};

const renderMelDetailLine = (line, index, onReferenceClick = null) => {
  const value = String(line || '').trim();
  const type = getMelDetailLineType(value);

  if (type === 'references-title') {
    return <div key={`${index}-${value}`} className="documents-mel-detail-line type-references-title"><span>{value}</span></div>;
  }

  const markerMatch = value.match(/^(\([om]\)(?:\([om]\))?|\d+\))\s*(.*)$/i);
  if (markerMatch) {
    return (
      <div key={`${index}-${value}`} className={`documents-mel-detail-line type-${type}`}>
        <span className="documents-mel-detail-marker">{markerMatch[1]}</span>
        {renderDocumentReference(
          value,
          <span>{renderMelDetailInlineText(markerMatch[2])}</span>,
          onReferenceClick
        )}
      </div>
    );
  }

  return (
    <div key={`${index}-${value}`} className={`documents-mel-detail-line type-${type}`}>
      {renderDocumentReference(value, renderMelDetailInlineText(value), onReferenceClick)}
    </div>
  );
};

export const renderMelDetailText = (text, richBlocks = [], onReferenceClick = null) => {
  const richContent = renderRichDocumentBlocks(richBlocks, 'mel', false, onReferenceClick);
  if (richContent) return richContent;

  const lines = String(text || '')
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean);

  if (!lines.length) return <p>No details available.</p>;

  return (
    <div className="documents-mel-detail-text">
      {lines.map((line, index) => renderMelDetailLine(line, index, onReferenceClick))}
    </div>
  );
};

const isAllApplicability = (items) => (
  !items.length || (items.length === 1 && /^ALL$/i.test(items[0]))
);

const isGeneralizedApplicability = (items) => (
  items.length > 0 && items.every(item => /^A3\d{2}-[A-Z0-9]+$/i.test(item))
);

const renderApplicabilitySection = (items, label, className = '') => {
  const allAircraft = isAllApplicability(items);
  const generalized = isGeneralizedApplicability(items);

  return (
    <div className={`documents-detail-section ${className}`.trim()}>
      <span>{label}</span>
      {allAircraft ? (
        <p>ALL</p>
      ) : generalized ? (
        <div className="documents-applicability-list documents-applicability-types">
          {items.map(item => <span key={item} className="documents-applicability-chip">{item}</span>)}
        </div>
      ) : (
        <details className="documents-applicability-dropdown">
          <summary>
            <span>{items.length} aircraft</span>
            <span className="documents-applicability-summary-action">Show list</span>
          </summary>
          <div className="documents-applicability-list">
            {items.map(item => <span key={item} className="documents-applicability-chip">{item}</span>)}
          </div>
        </details>
      )}
    </div>
  );
};

export const renderFullscreenDocument = (record, recordType, applicability, onReferenceClick = null) => {
  const procedureMode = recordType === 'procedures';
  const entryMode = recordType === 'entries';

  if (entryMode) {
    return (
      <div className="documents-fullscreen-document is-entry">
        <article className="documents-document-sheet is-entry">
          <div className="documents-sheet-heading">
            <div className="documents-sheet-base-ref">ATA {record.ata_chapter}</div>
            <div className="documents-sheet-base-title">{record.ata_chapter_title}</div>
          </div>
          <div className="documents-entry-alert">
            {renderStyledPdfSpans(record.heading_spans) || `${record.alert_type || 'ECAM Alert'}: ${record.title}`}
          </div>
          {renderApplicabilitySection(
            applicability,
            'Applicable to',
            'documents-document-applicability documents-fullscreen-scope'
          )}
          <section className="documents-sheet-content documents-entry-content">
            {renderRichDocumentBlocks(record.content_blocks, 'entry', false, onReferenceClick) || <p>No dispatch conditions available.</p>}
          </section>
        </article>
      </div>
    );
  }

  const baseReference = record.base_number || record.reference;
  const baseTitle = record.base_title || record.title;
  const showEntryTitle = record.title && record.title !== baseTitle;

  return (
    <div className={`documents-fullscreen-document ${procedureMode ? 'is-procedure' : 'is-mel'}`}>
      <article className={`documents-document-sheet ${procedureMode ? 'is-procedure' : 'is-mel'}`}>
        <div className="documents-sheet-heading">
          <div className="documents-sheet-base-ref">{baseReference}</div>
          <div className="documents-sheet-base-title">{baseTitle}</div>
        </div>

        {renderApplicabilitySection(
          applicability,
          'Applicable to',
          'documents-document-applicability documents-fullscreen-scope'
        )}

        <div className="documents-sheet-entry-heading">
          <strong>{record.reference}</strong>
          {showEntryTitle ? <span>{record.title}</span> : null}
        </div>

        {procedureMode ? (
          <section className="documents-sheet-content documents-fullscreen-procedure">
            <div className="documents-sheet-section-title">Operational procedure</div>
            {renderProcedureText(record.procedure_text, true, record.procedure_blocks, onReferenceClick)}
          </section>
        ) : (
          <>
            {renderMelRequirementTable(record, 'documents-fullscreen-limits')}
            <section className="documents-detail-section documents-sheet-content documents-fullscreen-details">
              <span>Remarks and conditions</span>
              {renderMelDetailText(record.remarks, record.remarks_blocks, onReferenceClick)}
            </section>
          </>
        )}
      </article>
    </div>
  );
};

const getRecordKey = (item) => [
  item.operator_code || 'WZZ',
  item.entry_id || item.reference || '',
  item.applicability || '',
  item.title || ''
].join('|');

const getPinnedId = ({ documentType, operatorCode, recordKey }) => (
  [documentType || 'mel', operatorCode || 'WZZ', recordKey || ''].join('|')
);

const loadPinnedMels = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(PINNED_MELS_STORAGE_KEY) || '[]');
    return Array.isArray(parsed)
      ? parsed.filter(item => item?.id && item?.recordKey && item?.documentType && item?.operatorCode)
      : [];
  } catch {
    return [];
  }
};

const savePinnedMels = (pins) => {
  try {
    localStorage.setItem(PINNED_MELS_STORAGE_KEY, JSON.stringify(pins));
  } catch {
    // Pins are a convenience; ignore storage failures.
  }
};

const sortRecords = (items) => [...items].sort((a, b) => (
  String(a.reference || a.title || '').localeCompare(String(b.reference || b.title || ''), undefined, { numeric: true })
));

const createDocumentNode = (code, title) => ({
  code,
  title: title || code,
  count: 0,
  sections: new Map(),
  subsections: new Map(),
  bases: new Map(),
  items: []
});

const sortDocumentNodes = nodes => [...nodes]
  .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));

const buildDocumentTree = (items, entryMode = false) => {
  const chapterMap = new Map();

  items.forEach(item => {
    const chapterCode = item.ata_chapter || '00';
    if (!chapterMap.has(chapterCode)) {
      chapterMap.set(chapterCode, createDocumentNode(chapterCode, item.ata_chapter_title || `ATA ${chapterCode}`));
    }
    const chapter = chapterMap.get(chapterCode);
    chapter.count += 1;
    if (entryMode) {
      chapter.items.push(item);
      return;
    }

    const sectionCode = item.ata_section || chapterCode;
    if (!chapter.sections.has(sectionCode)) {
      chapter.sections.set(sectionCode, createDocumentNode(sectionCode, item.ata_section_title || sectionCode));
    }
    const section = chapter.sections.get(sectionCode);
    section.count += 1;

    const subsectionCode = item.ata_subsection || '';
    const baseContainer = subsectionCode
      ? (() => {
          if (!section.subsections.has(subsectionCode)) {
            section.subsections.set(subsectionCode, createDocumentNode(
              subsectionCode,
              item.ata_subsection_title || subsectionCode
            ));
          }
          const subsection = section.subsections.get(subsectionCode);
          subsection.count += 1;
          return subsection;
        })()
      : section;

    const baseCode = item.base_number || item.reference || item.title;
    if (!baseContainer.bases.has(baseCode)) {
      baseContainer.bases.set(baseCode, createDocumentNode(baseCode, item.base_title || item.title));
    }
    const base = baseContainer.bases.get(baseCode);
    base.count += 1;
    base.items.push(item);
  });

  return sortDocumentNodes(chapterMap.values()).map(chapter => ({
      ...chapter,
      items: sortRecords(chapter.items),
      sections: sortDocumentNodes(chapter.sections.values()).map(section => ({
        ...section,
        subsections: sortDocumentNodes(section.subsections.values()).map(subsection => ({
          ...subsection,
          bases: sortDocumentNodes(subsection.bases.values())
        })),
        bases: sortDocumentNodes(section.bases.values())
      }))
    }));
};

const recordMatchesSearch = (item, query) => {
  if (!query) return true;
  return [
    item.reference,
    item.title,
    item.base_title,
    item.ata_chapter_title,
    item.ata_section_title,
    item.applicability,
    item.applicability_types,
    item.remarks,
    item.procedure_text,
    item.alert_type,
    item.entry_id,
    item.search_text
  ].filter(Boolean).join(' ').toLowerCase().includes(query);
};

const procedureActionEndStatePattern = /\b(?:OFF|ON|PULL|PUSH|AUTO|OVRD|OPEN|CLOSE|PRESS|RQRD|USE|SHUT|HI|NORM|MAN|CHECK|ATT|MCT|IDLE|REQUIRED|INOPERATIVE|FULL|TEST|DN|ALTN|SET|CENTERED|ZERO|ARMED|RED|PLACE|RELEASE)\s*$/i;

const isProcedureBulletPrefix = (value) =>
  /^(?:-|\.|\u2010|\u2011|\u2012|\u2013|\u2022)\s*/.test(value);

const stripProcedureBulletPrefix = (value) =>
  value.replace(/^(?:-|\.|\u2010|\u2011|\u2012|\u2013|\u2022)\s*/, '');

const isProcedureActionLine = (value) => {
  const clean = stripProcedureBulletPrefix(value);
  return (
    /\.{4,}/.test(clean) ||
    procedureActionEndStatePattern.test(clean) ||
    /\b(?:pb-sw|C\/B|knob|switch|lever|selector|valve|button|handle|guard)\b/i.test(clean) ||
    /\b(?:OFF|ON|PULL|PUSH|SET|CHECK|DISREGARD|CLEAR|CONFIRM|APPLY)\b/i.test(clean)
  );
};

const getProcedureLineType = (line) => {
  const value = String(line || '').trim();
  if (!value) return 'blank';
  if (/^note:/i.test(value)) return 'note';
  if (/^\|.+\|$/.test(value)) return 'table';
  if (/^(general information|flight preparation\/limitations|during cockpit preparation|after engine start|before takeoff|for takeoff|in flight|for landing|after landing|before leaving the aircraft)$/i.test(value)) {
    return 'heading';
  }
  if (/^[A-Z0-9 /()&.-]{4,}$/.test(value) && /[A-Z]/.test(value) && value.length <= 48) return 'subheading';
  if (isProcedureBulletPrefix(value)) return 'check';
  if (isProcedureActionLine(value)) return 'action';
  return 'body';
};

const getRichProcedureLineType = (blocks, index, plain) => {
  const baseType = getProcedureLineType(plain);
  if (!['body', 'action'].includes(baseType)) return baseType;

  const currentIndent = Number(blocks[index]?.indent) || 0;
  for (let previousIndex = index - 1; previousIndex >= Math.max(0, index - 3); previousIndex -= 1) {
    const previous = blocks[previousIndex];
    if (!previous || previous.type !== 'line') break;
    const previousPlain = previous.plain || previous.text || '';
    const previousType = getProcedureLineType(previousPlain);
    if (['heading', 'subheading'].includes(previousType)) break;

    const previousIndent = Number(previous.indent) || 0;
    if (previousType === 'check' && currentIndent > previousIndent + 4) return 'continuation';
    if (previousType === 'note' && currentIndent > previousIndent + 4) return 'note-continuation';
  }

  return baseType;
};

const parseProcedureTableRow = (line) => String(line || '')
  .trim()
  .replace(/^\|/, '')
  .replace(/\|$/, '')
  .split('|')
  .map(cell => cell.trim());

const buildProcedureBlocks = (text) => {
  const lines = String(text || '')
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean);

  const blocks = [];
  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];

    if (getProcedureLineType(line) === 'table') {
      const rows = [];
      while (index < lines.length && getProcedureLineType(lines[index]) === 'table') {
        rows.push(parseProcedureTableRow(lines[index]));
        index += 1;
      }
      index -= 1;
      blocks.push({ type: 'table', rows });
    } else {
      blocks.push({ type: 'line', line, lineType: getProcedureLineType(line) });
    }
  }

  return blocks;
};

const renderProcedureInlineText = (text) => String(text || '');

const renderProcedureText = (text, compact = false, richBlocks = [], onReferenceClick = null) => {
  const richContent = renderRichDocumentBlocks(richBlocks, 'procedure', compact, onReferenceClick);
  if (richContent) return richContent;

  return (
    <div className={`documents-procedure-text ${compact ? 'compact' : ''}`}>
      {buildProcedureBlocks(text)
        .map((block, index) => {
          if (block.type === 'table') {
            return (
              <div key={`table-${index}`} className="documents-procedure-table-wrap">
                <table className="documents-procedure-table">
                  <tbody>
                    {block.rows.map((row, rowIndex) => (
                      <tr key={`${rowIndex}-${row.join('|')}`}>
                        {row.map((cell, cellIndex) => (
                          <td key={`${cellIndex}-${cell}`}>
                            {renderDocumentReference(cell, renderProcedureInlineText(cell), onReferenceClick)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          }

          const { line, lineType: type } = block;
          return (
            <div key={`${index}-${line.slice(0, 24)}`} className={`documents-procedure-line type-${type}`}>
              {renderDocumentReference(line, renderProcedureInlineText(line), onReferenceClick)}
            </div>
          );
        })}
    </div>
  );
};

const DocumentsFolderPage = () => {
  const { aircraft } = useAuth();
  const [restoredView] = useState(loadDocumentsView);
  const [activeOperator, setActiveOperator] = useState(() => (
    OPERATORS.some(operator => operator.code === restoredView.activeOperator)
      ? restoredView.activeOperator
      : getOperatorForRegistration(aircraft?.registration)
  ));
  const [documentType, setDocumentType] = useState(() => (
    DOCUMENT_TYPES.some(type => type.id === restoredView.documentType) ? restoredView.documentType : 'mel'
  ));
  const [itemsByOperator, setItemsByOperator] = useState({ WZZ: [], WMT: [] });
  const [entriesByOperator, setEntriesByOperator] = useState({ WZZ: [], WMT: [] });
  const [proceduresByOperator, setProceduresByOperator] = useState({ WZZ: [], WMT: [] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState(() => String(restoredView.search || ''));
  const [documentPath, setDocumentPath] = useState(() => ({
    ...EMPTY_DOCUMENT_PATH,
    ...(restoredView.documentPath || {})
  }));
  const [selectedRecordKey, setSelectedRecordKey] = useState(() => String(restoredView.selectedRecordKey || ''));
  const [readerOpen, setReaderOpen] = useState(() => Boolean(restoredView.readerOpen));
  const [readerClosing, setReaderClosing] = useState(false);
  const [pinnedMels, setPinnedMels] = useState(loadPinnedMels);
  const [pinnedViewOpen, setPinnedViewOpen] = useState(() => Boolean(restoredView.pinnedViewOpen));
  const [pinnedViewClosing, setPinnedViewClosing] = useState(false);
  const [selectedPinnedId, setSelectedPinnedId] = useState(() => String(restoredView.selectedPinnedId || ''));
  const itemListRef = useRef(null);
  const detailDocumentRef = useRef(null);
  const readerContentRef = useRef(null);
  const pinnedContentRef = useRef(null);
  const scrollSaveTimerRef = useRef(null);
  const readerCloseTimerRef = useRef(null);
  const pinnedCloseTimerRef = useRef(null);
  const scrollPositionsRef = useRef({ ...(restoredView.scrollPositions || {}) });
  const viewStateRef = useRef({});

  viewStateRef.current = {
    activeOperator,
    documentType,
    search,
    documentPath,
    selectedRecordKey,
    readerOpen,
    pinnedViewOpen,
    selectedPinnedId,
    scrollPositions: scrollPositionsRef.current
  };

  const rememberScroll = (key, event) => {
    scrollPositionsRef.current[key] = event.currentTarget.scrollTop;
    window.clearTimeout(scrollSaveTimerRef.current);
    scrollSaveTimerRef.current = window.setTimeout(() => saveDocumentsView(viewStateRef.current), 120);
  };

  useEffect(() => {
    saveDocumentsView(viewStateRef.current);
  }, [activeOperator, documentPath, documentType, pinnedViewOpen, readerOpen, search, selectedPinnedId, selectedRecordKey]);

  useEffect(() => {
    const persistView = () => saveDocumentsView(viewStateRef.current);
    window.addEventListener('pagehide', persistView);
    return () => {
      window.removeEventListener('pagehide', persistView);
      window.clearTimeout(scrollSaveTimerRef.current);
      window.clearTimeout(readerCloseTimerRef.current);
      window.clearTimeout(pinnedCloseTimerRef.current);
      persistView();
    };
  }, []);

  useEffect(() => {
    savePinnedMels(pinnedMels);
  }, [pinnedMels]);

  useEffect(() => {
    let cancelled = false;

    const loadDocuments = async () => {
      setLoading(true);
      setLoadError('');

      try {
        const [wzzItemsRes, wmtItemsRes, wzzEntriesRes, wmtEntriesRes, wzzProceduresRes, wmtProceduresRes] = await Promise.all([
          getMELItems({ operator: 'WZZ', limit: 2000 }),
          getMELItems({ operator: 'WMT', limit: 2000 }),
          getMELEntries({ operator: 'WZZ', limit: 2000 }),
          getMELEntries({ operator: 'WMT', limit: 2000 }),
          getMELProcedures({ operator: 'WZZ', limit: 2000 }),
          getMELProcedures({ operator: 'WMT', limit: 2000 })
        ]);

        if (cancelled) return;
        setItemsByOperator({
          WZZ: sortRecords(wzzItemsRes.data.items || []),
          WMT: sortRecords(wmtItemsRes.data.items || [])
        });
        setEntriesByOperator({
          WZZ: sortRecords(wzzEntriesRes.data.entries || []),
          WMT: sortRecords(wmtEntriesRes.data.entries || [])
        });
        setProceduresByOperator({
          WZZ: sortRecords(wzzProceduresRes.data.procedures || []),
          WMT: sortRecords(wmtProceduresRes.data.procedures || [])
        });
      } catch (err) {
        try {
          const fallbackRes = await fetch(`${process.env.PUBLIC_URL || ''}/mel_items.json`);
          if (!fallbackRes.ok) throw new Error(`MEL fallback failed: ${fallbackRes.status}`);
          const fallbackData = await fallbackRes.json();
          const items = fallbackData.items || [];
          const entries = fallbackData.entries || [];
          const procedures = fallbackData.procedures || [];

          if (cancelled) return;
          setItemsByOperator({
            WZZ: sortRecords(items.filter(item => String(item.operator_code || 'WZZ').toUpperCase() === 'WZZ')),
            WMT: sortRecords(items.filter(item => String(item.operator_code || 'WZZ').toUpperCase() === 'WMT'))
          });
          setEntriesByOperator({
            WZZ: sortRecords(entries.filter(item => String(item.operator_code || 'WZZ').toUpperCase() === 'WZZ')),
            WMT: sortRecords(entries.filter(item => String(item.operator_code || 'WZZ').toUpperCase() === 'WMT'))
          });
          setProceduresByOperator({
            WZZ: sortRecords(procedures.filter(item => String(item.operator_code || 'WZZ').toUpperCase() === 'WZZ')),
            WMT: sortRecords(procedures.filter(item => String(item.operator_code || 'WZZ').toUpperCase() === 'WMT'))
          });
        } catch (fallbackErr) {
          if (!cancelled) {
            setItemsByOperator({ WZZ: [], WMT: [] });
            setEntriesByOperator({ WZZ: [], WMT: [] });
            setProceduresByOperator({ WZZ: [], WMT: [] });
            setLoadError('MEL documents unavailable');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadDocuments();
    return () => {
      cancelled = true;
    };
  }, []);

  const activeRecords = useMemo(() => (
    documentType === 'entries'
      ? entriesByOperator[activeOperator] || []
      : documentType === 'procedures'
      ? proceduresByOperator[activeOperator] || []
      : itemsByOperator[activeOperator] || []
  ), [activeOperator, documentType, entriesByOperator, itemsByOperator, proceduresByOperator]);
  const activeOperatorMeta = OPERATORS.find(operator => operator.code === activeOperator) || OPERATORS[0];
  const activeDocumentMeta = DOCUMENT_TYPES.find(type => type.id === documentType) || DOCUMENT_TYPES[0];
  const isEntryMode = documentType === 'entries';
  const documentTree = useMemo(() => buildDocumentTree(activeRecords, isEntryMode), [activeRecords, isEntryMode]);
  const chapterNode = documentTree.find(node => node.code === documentPath.chapter) || null;
  const sectionNode = chapterNode?.sections.find(node => node.code === documentPath.section) || null;
  const subsectionNode = sectionNode?.subsections.find(node => node.code === documentPath.subsection) || null;
  const baseContainer = subsectionNode || sectionNode;
  const baseNode = baseContainer?.bases.find(node => node.code === documentPath.base) || null;
  const searchQuery = search.trim().toLowerCase();

  const visibleRecords = useMemo(() => {
    if (searchQuery) {
      return sortRecords(activeRecords.filter(item => recordMatchesSearch(item, searchQuery)));
    }
    if (isEntryMode) return sortRecords(chapterNode?.items || []);
    return sortRecords(baseNode?.items || []);
  }, [activeRecords, baseNode, chapterNode, isEntryMode, searchQuery]);

  const currentGroups = useMemo(() => {
    if (searchQuery || visibleRecords.length) return [];
    if (!chapterNode) return documentTree;
    if (isEntryMode) return [];
    if (!sectionNode) return chapterNode.sections;
    if (!subsectionNode && sectionNode.subsections.length) return sectionNode.subsections;
    if (!baseNode) return baseContainer?.bases || [];
    return [];
  }, [baseContainer, baseNode, chapterNode, documentTree, isEntryMode, searchQuery, sectionNode, subsectionNode, visibleRecords.length]);

  const selectedRecord = useMemo(() => (
    visibleRecords.find(item => getRecordKey(item) === selectedRecordKey)
    || activeRecords.find(item => getRecordKey(item) === selectedRecordKey)
    || null
  ), [activeRecords, selectedRecordKey, visibleRecords]);
  const pinnedRecords = useMemo(() => (
    pinnedMels
      .map(pin => {
        const sourceRecords = pin.documentType === 'entries'
          ? entriesByOperator[pin.operatorCode] || []
          : pin.documentType === 'procedures'
            ? proceduresByOperator[pin.operatorCode] || []
            : itemsByOperator[pin.operatorCode] || [];
        const record = sourceRecords.find(item => getRecordKey(item) === pin.recordKey) || null;
        return {
          ...pin,
          record,
          reference: record?.reference || pin.reference,
          title: record?.title || record?.base_title || pin.title,
          subtitle: record
            ? pin.documentType === 'entries'
              ? `MEL Entry - ATA ${record.ata_chapter || ''}`
              : pin.documentType === 'procedures'
              ? `Procedure - ${record.ata_section || ''}`
              : `MEL ${record.mel_type || '-'} - ${record.ata_section || ''}`
            : pin.subtitle || `${pin.documentType === 'entries' ? 'MEL Entry' : pin.documentType === 'procedures' ? 'Procedure' : 'MEL Item'}`
        };
      })
      .sort((a, b) => String(b.pinnedAt || '').localeCompare(String(a.pinnedAt || '')))
  ), [entriesByOperator, itemsByOperator, pinnedMels, proceduresByOperator]);
  const pinnedIdSet = useMemo(() => new Set(pinnedMels.map(pin => pin.id)), [pinnedMels]);
  const selectedRecordPinId = selectedRecord
    ? getPinnedId({
        documentType,
        operatorCode: selectedRecord.operator_code || activeOperator,
        recordKey: getRecordKey(selectedRecord)
      })
    : '';
  const selectedRecordPinned = selectedRecordPinId ? pinnedIdSet.has(selectedRecordPinId) : false;
  const selectedPinnedRecord = pinnedRecords.find(pin => pin.id === selectedPinnedId) || null;
  const selectedPinnedDetailApplicability = formatApplicability(
    selectedPinnedRecord?.record?.applicability_types || selectedPinnedRecord?.record?.applicability
  );

  useEffect(() => {
    if (!loading && selectedRecordKey && !activeRecords.some(item => getRecordKey(item) === selectedRecordKey)) {
      setSelectedRecordKey('');
    }
  }, [activeRecords, loading, selectedRecordKey]);

  useEffect(() => {
    if (loading) return undefined;
    const frame = window.requestAnimationFrame(() => {
      if (itemListRef.current) itemListRef.current.scrollTop = Number(restoredView.scrollPositions?.list) || 0;
      if (detailDocumentRef.current) detailDocumentRef.current.scrollTop = Number(restoredView.scrollPositions?.detail) || 0;
      if (restoredView.readerOpen && readerContentRef.current) {
        readerContentRef.current.scrollTop = Number(restoredView.scrollPositions?.reader) || 0;
      }
      if (restoredView.pinnedViewOpen && pinnedContentRef.current) {
        pinnedContentRef.current.scrollTop = Number(restoredView.scrollPositions?.pinned) || 0;
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [loading, restoredView]);

  const resetDocumentPosition = () => {
    setSearch('');
    setDocumentPath(EMPTY_DOCUMENT_PATH);
    setSelectedRecordKey('');
    setReaderOpen(false);
  };

  const handleOperatorChange = (operatorCode) => {
    setActiveOperator(operatorCode);
    resetDocumentPosition();
  };

  const handlePinnedOpen = (pin) => {
    if (!pin.record) return;
    setSelectedPinnedId(pin.id);
  };

  const handleOpenReader = () => {
    window.clearTimeout(readerCloseTimerRef.current);
    setReaderClosing(false);
    setReaderOpen(true);
  };

  const handleCloseReader = () => {
    if (readerClosing) return;
    setReaderClosing(true);
    readerCloseTimerRef.current = window.setTimeout(() => {
      setReaderOpen(false);
      setReaderClosing(false);
    }, 180);
  };

  const handleOpenPinned = () => {
    window.clearTimeout(pinnedCloseTimerRef.current);
    setPinnedViewClosing(false);
    setSelectedPinnedId('');
    setPinnedViewOpen(true);
  };

  const handleClosePinned = () => {
    if (pinnedViewClosing) return;
    setPinnedViewClosing(true);
    pinnedCloseTimerRef.current = window.setTimeout(() => {
      setPinnedViewOpen(false);
      setPinnedViewClosing(false);
      setSelectedPinnedId('');
    }, 180);
  };

  const handleRemovePinned = (event, pinId) => {
    event.stopPropagation();
    setPinnedMels(current => current.filter(pin => pin.id !== pinId));
    if (selectedPinnedId === pinId) setSelectedPinnedId('');
  };

  const handleToggleSelectedPin = () => {
    if (!selectedRecord) return;

    const operatorCode = selectedRecord.operator_code || activeOperator;
    const recordKey = getRecordKey(selectedRecord);
    const id = getPinnedId({ documentType, operatorCode, recordKey });

    setPinnedMels(current => {
      if (current.some(pin => pin.id === id)) {
        return current.filter(pin => pin.id !== id);
      }

      const isEntry = documentType === 'entries';
      const isProcedure = documentType === 'procedures';
      return [
        {
          id,
          documentType,
          operatorCode,
          recordKey,
          reference: selectedRecord.reference || selectedRecord.title || '',
          title: selectedRecord.title || selectedRecord.base_title || '',
          subtitle: isEntry
            ? `MEL Entry - ATA ${selectedRecord.ata_chapter || ''}`
            : isProcedure
              ? `Procedure - ${selectedRecord.ata_section || ''}`
              : `MEL ${selectedRecord.mel_type || '-'} - ${selectedRecord.ata_section || ''}`,
          pinnedAt: new Date().toISOString()
        },
        ...current
      ];
    });
  };

  const handleDocumentTypeChange = (nextType) => {
    setDocumentType(nextType);
    resetDocumentPosition();
  };

  const handleDocumentReferenceClick = ({ documentType: targetType, reference }) => {
    const recordsByOperator = targetType === 'procedures' ? proceduresByOperator : itemsByOperator;
    const normalizedReference = String(reference || '').trim().toUpperCase();
    const findTarget = operatorCode => (recordsByOperator[operatorCode] || []).find(record => (
      String(record.reference || '').trim().toUpperCase() === normalizedReference
    )) || (recordsByOperator[operatorCode] || []).find(record => (
      String(record.base_number || '').trim().toUpperCase() === normalizedReference
    ));

    let targetOperator = activeOperator;
    let targetRecord = findTarget(targetOperator);
    if (!targetRecord) {
      targetOperator = OPERATORS.find(operator => findTarget(operator.code))?.code || activeOperator;
      targetRecord = findTarget(targetOperator);
    }

    setDocumentType(targetType);
    setActiveOperator(targetOperator);
    setDocumentPath(EMPTY_DOCUMENT_PATH);
    setSearch(normalizedReference);
    setSelectedRecordKey(targetRecord ? getRecordKey(targetRecord) : '');
    setPinnedViewOpen(false);
    setPinnedViewClosing(false);
    setSelectedPinnedId('');
    setReaderClosing(false);
    window.requestAnimationFrame(() => {
      if (itemListRef.current) itemListRef.current.scrollTop = 0;
      if (detailDocumentRef.current) detailDocumentRef.current.scrollTop = 0;
      if (readerContentRef.current) readerContentRef.current.scrollTop = 0;
    });
  };

  const handleGroupSelect = (node) => {
    setSearch('');
    setSelectedRecordKey('');
    if (!chapterNode) {
      setDocumentPath({ chapter: node.code, section: '', subsection: '', base: '' });
    } else if (!sectionNode) {
      setDocumentPath(current => ({ ...current, section: node.code, subsection: '', base: '' }));
    } else if (!subsectionNode && sectionNode.subsections.length) {
      setDocumentPath(current => ({ ...current, subsection: node.code, base: '' }));
    } else {
      setDocumentPath(current => ({ ...current, base: node.code }));
    }
  };

  const navigateDocumentPath = () => {
    setSelectedRecordKey('');
    if (baseNode) {
      setDocumentPath(current => ({ ...current, base: '' }));
    } else if (subsectionNode) {
      setDocumentPath(current => ({ ...current, subsection: '', base: '' }));
    } else if (sectionNode) {
      setDocumentPath(current => ({ ...current, section: '', subsection: '', base: '' }));
    } else if (chapterNode) {
      setDocumentPath(EMPTY_DOCUMENT_PATH);
    }
  };

  const detailApplicability = formatApplicability(
    selectedRecord?.applicability_types || selectedRecord?.applicability
  );
  const isProcedureMode = documentType === 'procedures';
  const indexTitle = searchQuery
    ? 'Search Results'
    : baseNode
      ? `${baseNode.code} ${baseNode.title}`
      : subsectionNode
        ? `${subsectionNode.code} ${subsectionNode.title}`
        : sectionNode
          ? `${sectionNode.code} ${sectionNode.title}`
          : chapterNode
            ? `${chapterNode.code} ${chapterNode.title}`
        : activeDocumentMeta.title;

  return (
    <div className="documents-page">
      <header className="documents-header">
        <div className="documents-heading">
          <div className="documents-title">Documents Folder</div>
          <div className="documents-subtitle">{activeDocumentMeta.title}</div>
        </div>
        <div className="documents-header-controls">
          <div className="documents-type-toggle" role="tablist" aria-label="Document type">
            {DOCUMENT_TYPES.map(type => (
              <button
                key={type.id}
                type="button"
                role="tab"
                aria-selected={documentType === type.id}
                className={`documents-type-btn ${documentType === type.id ? 'active' : ''}`}
                onClick={() => handleDocumentTypeChange(type.id)}
              >
                {type.label}
              </button>
            ))}
          </div>
          <div className="documents-operator-toggle" role="group" aria-label="MEL operator">
            {OPERATORS.map(operator => (
              <button
                key={operator.code}
                type="button"
                className={`documents-operator-btn ${activeOperator === operator.code ? 'active' : ''}`}
                aria-pressed={activeOperator === operator.code}
                onClick={() => handleOperatorChange(operator.code)}
              >
                {operator.label}
              </button>
            ))}
          </div>
          <div className="documents-pinned-wrap">
            <button
              type="button"
              className={`documents-pinned-trigger ${pinnedViewOpen ? 'active' : ''}`}
              onClick={handleOpenPinned}
            >
              <span>Pinned MELs</span>
              <small>{pinnedRecords.length}</small>
            </button>
          </div>
        </div>
      </header>

      <div key={`${activeOperator}-${documentType}`} className="documents-body">
        <section className="documents-index-panel">
          <div className="documents-search-row">
            <input
              className="documents-search-input"
              value={search}
              onChange={event => {
                setSearch(event.target.value);
                setSelectedRecordKey('');
              }}
              placeholder={isEntryMode ? 'Search ECAM alerts...' : isProcedureMode ? 'Search procedures...' : 'Search MEL items...'}
            />
          </div>

          <div className="documents-browser-breadcrumb">
            <button
              type="button"
              onClick={navigateDocumentPath}
              disabled={Boolean(searchQuery) || !chapterNode}
            >
              {chapterNode && !searchQuery ? <span className="documents-browser-back-icon" aria-hidden="true" /> : null}
              <span>{indexTitle}</span>
            </button>
          </div>

          <div className="documents-index-head">
            <div>
              <span className="documents-index-title">{searchQuery ? 'Matching records' : currentGroups.length ? 'Select a category' : isEntryMode ? 'Select an ECAM alert' : 'Select a document'}</span>
              <span className="documents-index-operator">{activeOperatorMeta.name} - {activeDocumentMeta.label}</span>
            </div>
          </div>

          <div ref={itemListRef} className="documents-item-list" onScroll={event => rememberScroll('list', event)}>
            {loading ? (
              <div className="documents-empty">Loading...</div>
            ) : loadError ? (
              <div className="documents-empty">{loadError}</div>
            ) : currentGroups.length ? (
              <div className="documents-browser-grid">
                {currentGroups.map(node => (
                  <button key={node.code} type="button" className="documents-browser-node" onClick={() => handleGroupSelect(node)}>
                    <span className="documents-browser-node-code">{node.code}</span>
                    <span className="documents-browser-node-title">{node.title}</span>
                  </button>
                ))}
              </div>
            ) : !visibleRecords.length ? (
              <div className="documents-empty">{searchQuery ? 'No matching documents found.' : 'Select an ATA category.'}</div>
            ) : visibleRecords.map(item => {
              const itemRecordKey = getRecordKey(item);
              const itemPinned = pinnedIdSet.has(getPinnedId({
                documentType,
                operatorCode: item.operator_code || activeOperator,
                recordKey: itemRecordKey
              }));

              return (
                <button
                  key={itemRecordKey}
                  type="button"
                  className={`documents-item ${selectedRecord && getRecordKey(selectedRecord) === itemRecordKey ? 'active' : ''}`}
                  onClick={() => setSelectedRecordKey(itemRecordKey)}
                >
                  <span className="documents-item-ref">{isEntryMode ? `ATA ${item.ata_chapter}` : item.reference}</span>
                  <span className="documents-item-title">{item.title || item.base_title}</span>
                  <span className="documents-item-meta">
                    {!isEntryMode ? <span>{isProcedureMode ? 'Procedure' : `MEL ${item.mel_type || '-'}`}</span> : null}
                    {!isEntryMode ? <span>{item.ata_section}</span> : null}
                    {!isEntryMode ? <span>{isProcedureMode ? item.ata_section_title || 'Procedure' : item.placard_required ? 'Placard' : 'No placard'}</span> : null}
                    <span className="documents-item-applicability">{getMelApplicabilityTag(item)}</span>
                    {itemPinned ? <span className="documents-item-pinned">Pinned</span> : null}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <section
          ref={detailDocumentRef}
          className={`documents-detail-panel ${isProcedureMode ? 'is-procedure' : ''} ${isEntryMode ? 'is-entry' : ''}`}
          onScroll={event => rememberScroll('detail', event)}
        >
          {selectedRecord ? (
            <>
              <div className="documents-detail-toolbar">
                <div className="documents-detail-kicker">{isEntryMode ? 'MEL Entry' : isProcedureMode ? 'Operational Procedure' : 'MEL Item'}</div>
                <div className="documents-detail-actions">
                  <button
                    type="button"
                    className={`documents-pin-btn ${selectedRecordPinned ? 'active' : ''}`}
                    onClick={handleToggleSelectedPin}
                  >
                    {selectedRecordPinned ? 'Pinned' : 'Pin'}
                  </button>
                  <button type="button" className="documents-reader-btn" onClick={handleOpenReader}>Full Screen</button>
                </div>
              </div>
              <div className="documents-detail-document">
                {renderFullscreenDocument(selectedRecord, documentType, detailApplicability, handleDocumentReferenceClick)}
              </div>
            </>
          ) : (
            <div className="documents-empty documents-detail-empty">
              {loading ? 'Loading...' : 'No document selected.'}
            </div>
          )}
        </section>
      </div>

      {readerOpen && selectedRecord ? (
        <div className={`documents-reader-overlay ${readerClosing ? 'is-closing' : ''}`}>
          <div className="documents-reader-shell">
            <header className="documents-reader-header">
              <div>
                <div className="documents-reader-reference">{selectedRecord.reference || selectedRecord.title}</div>
                <div className="documents-reader-title">{selectedRecord.title}</div>
              </div>
              <button type="button" className="documents-reader-close" onClick={handleCloseReader}>Close</button>
            </header>
            <div className="documents-reader-meta">
              <span>{activeOperatorMeta.name}</span>
            </div>
            <div ref={readerContentRef} className="documents-reader-content" onScroll={event => rememberScroll('reader', event)}>
              {renderFullscreenDocument(selectedRecord, documentType, detailApplicability, handleDocumentReferenceClick)}
            </div>
          </div>
        </div>
      ) : null}

      {pinnedViewOpen ? (
        <div className={`documents-pinned-overlay ${pinnedViewClosing ? 'is-closing' : ''}`}>
          <div className="documents-pinned-screen">
            <header className="documents-pinned-screen-header">
              <div>
                <div className="documents-pinned-screen-title">
                  {selectedPinnedRecord?.record ? selectedPinnedRecord.reference : 'Pinned MELs'}
                </div>
                <div className="documents-pinned-screen-subtitle">
                  {selectedPinnedRecord?.record
                    ? selectedPinnedRecord.title || selectedPinnedRecord.subtitle
                    : `${pinnedRecords.length} saved`}
                </div>
              </div>
              <div className="documents-pinned-screen-actions">
                {selectedPinnedRecord?.record ? (
                  <button type="button" className="documents-reader-close" onClick={() => setSelectedPinnedId('')}>Back</button>
                ) : (
                  <button
                    type="button"
                    className="documents-reader-close"
                    onClick={handleClosePinned}
                  >
                    Close
                  </button>
                )}
              </div>
            </header>

            <div
              key={selectedPinnedId || 'pinned-list'}
              ref={pinnedContentRef}
              className="documents-pinned-screen-content"
              onScroll={event => rememberScroll('pinned', event)}
            >
              {selectedPinnedRecord?.record ? (
                <div className="documents-pinned-reader">
                  {renderFullscreenDocument(
                    selectedPinnedRecord.record,
                    selectedPinnedRecord.documentType,
                    selectedPinnedDetailApplicability,
                    handleDocumentReferenceClick
                  )}
                </div>
              ) : pinnedRecords.length ? (
                <div className="documents-pinned-grid">
                  {pinnedRecords.map(pin => (
                    <article key={pin.id} className={`documents-pinned-card ${pin.record ? '' : 'missing'}`}>
                      <button
                        type="button"
                        className="documents-pinned-card-main"
                        onClick={() => handlePinnedOpen(pin)}
                        disabled={!pin.record}
                      >
                        <span className="documents-pinned-ref">{pin.reference || 'Pinned MEL'}</span>
                        <span className="documents-pinned-title">{pin.title || 'No title'}</span>
                        {pin.documentType !== 'procedures' || !pin.record ? (
                          <span className="documents-pinned-meta">{pin.record ? pin.subtitle : 'Not found in current MEL data'}</span>
                        ) : null}
                        {pin.record ? (
                          <span className="documents-item-applicability">{getMelApplicabilityTag(pin.record)}</span>
                        ) : null}
                      </button>
                      <button
                        type="button"
                        className="documents-pinned-remove"
                        onClick={event => handleRemovePinned(event, pin.id)}
                      >
                        Unpin
                      </button>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="documents-pinned-empty documents-pinned-screen-empty">
                  Select a MEL item or procedure, then pin it from the detail header.
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DocumentsFolderPage;
